/**
 * Date formatting utilities for consistent, polished timestamps throughout the app.
 */

/**
 * Format a date as a full, readable string.
 * Example: "January 12, 2026 at 11:58 PM"
 */
export function formatFullDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Format a date as a shorter readable string (no time).
 * Example: "January 12, 2026"
 */
export function formatShortDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Format a date as relative time (for past dates).
 * Examples: "just now", "5 minutes ago", "2 hours ago", "yesterday", "3 days ago"
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();

  // Handle future dates (clock skew)
  if (diffMs < 0) return "just now";

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffSeconds < 60) return "just now";
  if (diffMinutes === 1) return "1 minute ago";
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffHours === 1) return "1 hour ago";
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return "last week";
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 60) return "last month";
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;

  return formatShortDate(d);
}

/**
 * Format a future date as relative time.
 * Examples: "in 5 minutes", "in 2 hours", "tomorrow", "in 3 days", "in 1 week"
 */
export function formatFutureRelativeTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();

  // Handle past dates (shouldn't happen but just in case)
  if (diffMs < 0) return "now";

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffSeconds < 60) return "in less than a minute";
  if (diffMinutes === 1) return "in 1 minute";
  if (diffMinutes < 60) return `in ${diffMinutes} minutes`;
  if (diffHours === 1) return "in 1 hour";
  if (diffHours < 24) return `in ${diffHours} hours`;
  if (diffDays === 1) return "tomorrow";
  if (diffDays < 7) return `in ${diffDays} days`;
  if (diffDays < 14) return "in 1 week";
  if (diffDays < 30) return `in ${Math.floor(diffDays / 7)} weeks`;

  return formatShortDate(d);
}

/**
 * Smart date formatter that shows relative time for recent dates
 * and falls back to full date for older ones.
 * Threshold: 7 days
 */
export function formatSmartDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 7) {
    return formatRelativeTime(d);
  }
  return formatShortDate(d);
}
